import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext.tsx';
import { SidebarSection } from '../components/SidebarSection.tsx';
import { SidebarSectionsMenu, type SidebarSectionOption } from '../components/SidebarSectionsMenu.tsx';
import {
  SIDEBAR_SECTION_KEYS,
  type AiUsageStats,
  type BranchColorRule,
  type CherryPickEdgeDTO,
  type GraphDTO,
  type MergeRequestDTO,
  type RepoTicketsResponse,
  type SidebarSectionKey,
  type SummaryResponse,
} from '@clearflow/shared';
import { api } from '../lib/api.ts';
import { GitGraph } from '../features/graph/GitGraph.tsx';
import { MrTooltip } from '../components/MrTooltip.tsx';
import { CherryPickTooltip } from '../components/CherryPickTooltip.tsx';
import { JiraTicketTooltip } from '../components/JiraTicketTooltip.tsx';
import { BranchFilter, DEFAULT_COLLAPSED_BRANCH_GROUPS, type BranchEntry } from '../features/graph/BranchFilter.tsx';
import { RepoJiraTickets, type JiraTooltipTarget } from '../features/jira/RepoJiraTickets.tsx';
import { CommitSearchBar } from '../features/graph/CommitSearchBar.tsx';

// Icône "trait" maison (viewBox 16x16, stroke currentColor) — même convention que le reste de
// l'appli (App.tsx : SettingsIcon/SunIcon/MoonIcon, CommitContextMenu.tsx) plutôt que l'emoji 🎨
// d'origine, qui reste colorée quel que soit le thème et détonnait à côté des autres boutons de
// .sidebar-toolbar. Palette de peintre avec pouce, sans aplat de couleur : juste des ronds vides
// pour les taches de peinture.
function TraceabilityIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <circle cx="3" cy="3" r="1.6" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13" cy="8" r="1.6" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="3" cy="13" r="1.6" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4.5 3.6 11.5 7.4M4.5 12.4l7-3.8" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function PaletteIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="14" height="14">
      <path
        d="M8 1.6c-3.6 0-6.4 2.6-6.4 5.7 0 1.9 1.3 3.1 2.9 3.1h.7c.5 0 .8.4.8.8 0 .2-.1.4-.3.6-.3.3-.4.6-.4.9 0 .8.8 1.3 1.7 1.3 3.7 0 6.8-3 6.8-6.8 0-3.2-2.7-5.6-5.8-5.6Z"
        stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"
      />
      <circle cx="5" cy="6.2" r="0.9" stroke="currentColor" strokeWidth="1" />
      <circle cx="8" cy="4.6" r="0.9" stroke="currentColor" strokeWidth="1" />
      <circle cx="11" cy="6.2" r="0.9" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

function computeBranchEntries(graph: GraphDTO): BranchEntry[] {
  return graph.branches.map((b) => ({ ...b, isDeleted: false }));
}

function computePrimaryBranch(
  graph: GraphDTO,
  branchEntries: BranchEntry[],
): Map<string, string> {
  const commitMap = new Map(graph.commits.map((c) => [c.sha, c]));
  const priority = (b: BranchEntry) => {
    if (b.isDefault) return 0;
    if (/(^|\/)release($|\/)/.test(b.name)) return 1;
    return 2;
  };
  const sorted = [...branchEntries].sort((a, b) => priority(a) - priority(b));
  const primary = new Map<string, string>();
  for (const b of sorted) {
    let sha: string | undefined = b.headSha;
    while (sha && !primary.has(sha)) {
      primary.set(sha, b.name);
      sha = commitMap.get(sha)?.parents[0];
    }
  }
  return primary;
}

/**
 * For each branch, find the branch it was created from by walking back along the
 * first-parent chain until we hit a commit claimed by a different branch.
 * Returns a parent → [children] map.
 */
function computeBranchChildren(
  graph: GraphDTO,
  branchEntries: BranchEntry[],
  primary: Map<string, string>,
): Map<string, string[]> {
  const commitMap = new Map(graph.commits.map((c) => [c.sha, c]));

  const parentOf = new Map<string, string>();
  for (const b of branchEntries) {
    if (b.isDefault) continue;
    let sha: string | undefined = b.headSha;
    while (sha) {
      const c = commitMap.get(sha);
      const parent = c?.parents[0];
      if (!parent) break;
      const claim = primary.get(parent);
      if (claim && claim !== b.name) {
        parentOf.set(b.name, claim);
        break;
      }
      sha = parent;
    }
  }

  const children = new Map<string, string[]>();
  for (const [child, parent] of parentOf) {
    const arr = children.get(parent) ?? [];
    arr.push(child);
    children.set(parent, arr);
  }
  return children;
}
import {
  CommitContextMenu,
  type ContextMenuPosition,
} from '../features/graph/CommitContextMenu.tsx';
import {
  BranchContextMenu,
  type BranchMenuPosition,
} from '../features/graph/BranchContextMenu.tsx';
import {
  TagContextMenu,
  type TagContextMenuPosition,
} from '../features/graph/TagContextMenu.tsx';
import { CherryPickModal } from '../features/cherry-pick/CherryPickModal.tsx';
import { CreateBranchModal } from '../features/graph/CreateBranchModal.tsx';
import { CreateTagModal } from '../features/graph/CreateTagModal.tsx';

// Persisté par compte (routes/preferences.ts), pas par repo : les rubriques disponibles sont
// les mêmes partout, donc une préférence par repo n'apporterait rien de plus qu'un réglage
// utilisateur unique. Tant que la préférence n'a pas encore été chargée (ou n'a jamais été
// réglée), toutes les rubriques sont visibles par défaut — voir l'effet de sync plus bas.

// Hauteur (de la rubrique entière .sidebar-section, en-tête ~25px inclus — voir
// onSectionResizeMouseDown) en dessous de laquelle elle se replie plutôt que de continuer à
// rétrécir — même principe que les panneaux de la sidebar VS Code.
// Panneaux et modales ouverts à la demande (GU-358) : chargés à la première ouverture. Rendre ces
// trois composants dynamiques sort aussi de la page d'atterrissage leurs dépendances propres (dnd-kit pour
// les règles de couleur, react-markdown pour le résumé IA).
const BranchColorRulesModal = lazy(() =>
  import('../features/graph/BranchColorRulesModal.tsx').then((m) => ({ default: m.BranchColorRulesModal })),
);
const SummaryPanel = lazy(() => import('../features/ai/SummaryPanel.tsx').then((m) => ({ default: m.SummaryPanel })));
const AiHistoryModal = lazy(() => import('../features/ai/AiHistoryModal.tsx').then((m) => ({ default: m.AiHistoryModal })));

// Modale de traçabilité (~800 lignes avec la vue par version) ouverte à la demande : chargée à la première
// ouverture plutôt que dans le bundle de la page d'atterrissage (GU-358).
const TraceabilityModal = lazy(() =>
  import('../features/traceability/TraceabilityModal.tsx').then((m) => ({ default: m.TraceabilityModal })),
);
// Même raison : modal de configuration CI (GU-378), ouverte à la demande.
const CiConfigModal = lazy(() =>
  import('../features/graph/CiConfigModal.tsx').then((m) => ({ default: m.CiConfigModal })),
);

const SECTION_MIN_HEIGHT: Record<SidebarSectionKey, number> = {
  branches: 105,
  mergeRequests: 73,
  cherryPicks: 73,
  jiraTickets: 73,
  claude: 73,
};
// Poids flex-grow/shrink par défaut avant tout drag — Branches démarre plus grande, cohérent
// avec sa liste de branches généralement plus longue que les autres rubriques.
const SECTION_DEFAULT_WEIGHT: Record<SidebarSectionKey, number> = {
  branches: 3,
  mergeRequests: 1,
  cherryPicks: 1,
  jiraTickets: 1,
  claude: 1,
};

// Une fois qu'une préférence de plié/déplié existe pour une rubrique, elle prime sur tout —
// y compris l'auto-ouverture de cherryPicks/mergeRequests selon le contenu du repo (effets plus
// bas). Cette dernière ne s'applique donc plus que tant que l'utilisateur n'a jamais touché la
// rubrique (préférence absente) ; un repli manuel devient définitif, cf. la conversation.
const PERSISTABLE_OPEN_KEYS: readonly SidebarSectionKey[] = SIDEBAR_SECTION_KEYS;

// Réplique le split-view de VS Code : une poignée redimensionne les deux rubriques adjacentes
// en complément l'une de l'autre (celle du dessus grandit quand celle du dessous rétrécit, ou
// inversement). Une fois qu'une rubrique atteint son minimum, le surplus de drag "traverse" et
// continue sur la rubrique suivante de la chaîne des rubriques ouvertes, jusqu'à ce qu'elle se
// replie complètement (juste l'en-tête visible) — plutôt que de bloquer le drag sur un plancher.
// Fonction pure et sans état : recalculée en entier à chaque mousemove à partir des hauteurs
// figées au mousedown, donc un repli en cours de drag n'a pas besoin d'être suivi entre deux
// appels.
// Rétrécit la chaîne en partant de `start` et en avançant de `step` (+1/-1) — porte la boucle
// commune aux deux branches (symétriques) de resolveSectionResize ci-dessous. Mute `heights`/
// `collapse` en place, renvoie la part de `amount` qui n'a pas pu être absorbée.
function shrinkChainFrom(
  chain: SidebarSectionKey[],
  heights: number[],
  collapse: Set<number>,
  start: number,
  step: 1 | -1,
  amount: number,
): number {
  let remaining = amount;
  let i = start;
  while (remaining > 0 && i >= 0 && i < chain.length) {
    const min = SECTION_MIN_HEIGHT[chain[i]];
    const avail = Math.max(heights[i] - min, 0);
    if (avail <= 0) {
      collapse.add(i);
      i += step;
      continue;
    }
    const take = Math.min(avail, remaining);
    heights[i] -= take;
    remaining -= take;
    if (heights[i] <= min + 0.5 && remaining > 0) {
      collapse.add(i);
      i += step;
    } else {
      break;
    }
  }
  return remaining;
}

function resolveSectionResize(
  chain: SidebarSectionKey[],
  startHeights: number[],
  pivot: number,
  delta: number,
): { heights: number[]; collapse: Set<number> } {
  const heights = [...startHeights];
  const collapse = new Set<number>();

  if (delta > 0) {
    // Glissé vers le bas : rétrécit à partir de `pivot` (la rubrique sous la poignée) vers la
    // fin de la chaîne ; ce qui a pu être libéré agrandit la rubrique juste au-dessus.
    const remaining = shrinkChainFrom(chain, heights, collapse, pivot, 1, delta);
    const freed = delta - remaining;
    if (pivot - 1 >= 0) heights[pivot - 1] += freed;
  } else if (delta < 0) {
    // Glissé vers le haut : symétrique, rétrécit en remontant depuis `pivot - 1`.
    const remaining = shrinkChainFrom(chain, heights, collapse, pivot - 1, -1, -delta);
    const freed = -delta - remaining;
    if (pivot < chain.length) heights[pivot] += freed;
  }

  return { heights, collapse };
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

// 4 décimales sous 1 centime — l'usage de dev/test tourne facilement sur des montants à moins de
// $0.01, où .toFixed(2) afficherait "$0.00" pour tout coût non nul.
function formatCostUsd(cost: number): string {
  return cost > 0 && cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(2)}`;
}

// ── Rubriques de la sidebar, extraites de RepoViewPage (S3776 : complexité cognitive) ───────────
// Chaque `{condition && (...)}`/ternaire écrit directement dans le JSX de retour de RepoViewPage
// compte contre SA complexité — contrairement à ceux nichés dans une fonction séparée (un composant
// ici). Pur découpage : aucune logique déplacée, mêmes props qu'avant, mêmes callbacks.

interface SidebarResizableSectionProps {
  hasPriorSection: boolean;
  onResizeMouseDown: (e: React.MouseEvent) => void;
  title: string;
  open: boolean;
  onToggleOpen: () => void;
  style: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  rootRef: (el: HTMLDivElement | null) => void;
  children: ReactNode;
}

// La poignée de redimensionnement et la SidebarSection qu'elle précède ne sont qu'un seul geste
// visuel, pas deux conditions indépendantes — regroupées ici pour ne compter qu'une fois dans le
// JSX de RepoViewPage plutôt que deux (`hasPriorSection && ...` imbriqué dans `visible && ...`).
function SidebarResizableSection({
  hasPriorSection, onResizeMouseDown, title, open, onToggleOpen, style, bodyStyle, rootRef, children,
}: SidebarResizableSectionProps) {
  return (
    <>
      {hasPriorSection && (
        <div className="sidebar-section-resizer" onMouseDown={onResizeMouseDown} title="Redimensionner" />
      )}
      <SidebarSection title={title} open={open} onToggleOpen={onToggleOpen} style={style} bodyStyle={bodyStyle} rootRef={rootRef}>
        {children}
      </SidebarSection>
    </>
  );
}

function MergeRequestsSectionBody({
  mrQuery, mrs, onHoverRow, onLeaveRow,
}: {
  mrQuery: { isLoading: boolean; isError: boolean; error: unknown };
  mrs: MergeRequestDTO[];
  onHoverRow: (mr: MergeRequestDTO, rowRect: DOMRect) => void;
  onLeaveRow: () => void;
}) {
  if (mrQuery.isLoading) return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Chargement…</p>;
  if (mrQuery.isError) return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Erreur : {(mrQuery.error as Error).message}</p>;
  if (mrs.length === 0) return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Aucune MR ouverte.</p>;
  return (
    <ul className="mr-list">
      {mrs.map((mr) => (
        <li key={mr.iid}>
          <a
            className="mr-row"
            href={mr.webUrl}
            target="_blank"
            rel="noreferrer"
            onMouseEnter={(e) => onHoverRow(mr, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={onLeaveRow}
          >
            <div className="mr-row-title">
              <code className="mr-row-iid">!{mr.iid}</code>
              {mr.draft && <span className="badge mr-draft-badge">Draft</span>}
              <span className="mr-row-title-text">{mr.title}</span>
            </div>
            <div className="mr-row-meta muted">
              <span className="mr-row-branches">{mr.sourceBranch} → {mr.targetBranch}</span>
              <span>{mr.authorName}</span>
            </div>
          </a>
        </li>
      ))}
    </ul>
  );
}

function CherryPicksSectionBody({
  cherryPicks, primaryBranchOf, selectedCherryPickId, onSelect, onHoverRow, onLeaveRow,
}: {
  cherryPicks: CherryPickEdgeDTO[];
  primaryBranchOf: Map<string, string>;
  selectedCherryPickId: string | null;
  onSelect: (id: string | null) => void;
  onHoverRow: (cp: CherryPickEdgeDTO, rowRect: DOMRect) => void;
  onLeaveRow: () => void;
}) {
  if (cherryPicks.length === 0) return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Aucun lien manuel.</p>;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12 }}>
      {cherryPicks.map((c) => {
        const srcBranch = primaryBranchOf.get(c.sourceSha);
        const tgtBranch = primaryBranchOf.get(c.targetSha);
        const isSelected = c.id === selectedCherryPickId;
        return (
          <li
            key={c.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(isSelected ? null : c.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(isSelected ? null : c.id);
              }
            }}
            onMouseEnter={(e) => onHoverRow(c, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={onLeaveRow}
            style={{
              marginBottom: 3,
              padding: '3px 6px',
              borderRadius: 4,
              cursor: 'pointer',
              background: isSelected ? 'rgba(255,152,0,0.15)' : 'transparent',
              border: isSelected ? '1px solid rgba(255,152,0,0.5)' : '1px solid transparent',
            }}
          >
            <code>{c.sourceSha.slice(0, 8)}</code>
            {srcBranch && <span className="muted"> ({srcBranch})</span>}
            {' → '}
            <code>{c.targetSha.slice(0, 8)}</code>
            {tgtBranch && <span className="muted"> ({tgtBranch})</span>}
          </li>
        );
      })}
    </ul>
  );
}

function JiraTicketsSectionBody({
  jiraTicketsQuery, tickets, onHoverTicket, onLeaveTicket,
}: {
  jiraTicketsQuery: { isLoading: boolean; isError: boolean; error: unknown };
  tickets: RepoTicketsResponse;
  onHoverTicket: (ticket: JiraTooltipTarget, rowRect: DOMRect) => void;
  onLeaveTicket: () => void;
}) {
  if (jiraTicketsQuery.isLoading) return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Chargement…</p>;
  if (jiraTicketsQuery.isError) return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Erreur : {(jiraTicketsQuery.error as Error).message}</p>;
  return <RepoJiraTickets tickets={tickets} onHoverTicket={onHoverTicket} onLeaveTicket={onLeaveTicket} />;
}

function ClaudeUsageSectionBody({ usage, onShowHistory }: { usage: AiUsageStats; onShowHistory: () => void }) {
  const pct = usage.dailyTokenQuota > 0
    ? Math.max(0, Math.min(100, (usage.remainingTokensToday / usage.dailyTokenQuota) * 100))
    : 0;
  return (
    <>
      <div className="ai-usage-limits">
        <span className="muted" title="Modèle utilisé">{usage.model}</span>
        <span className="muted" title="Taille max du diff envoyé à Claude">diff ≤ {formatTokens(usage.maxDiffChars)} chars</span>
      </div>
      <div className="ai-usage-quota" title="Quota de tokens IA par jour et par utilisateur, remis à zéro chaque jour">
        <div className="ai-usage-quota-bar">
          <div
            className="ai-usage-quota-fill"
            style={{ width: `${pct}%`, background: pct <= 20 ? 'var(--danger)' : 'var(--success)' }}
          />
        </div>
        <span className="muted" style={{ fontSize: 12 }}>
          {formatTokens(usage.remainingTokensToday)} / {formatTokens(usage.dailyTokenQuota)} restants aujourd'hui
        </span>
      </div>
      <div style={{ marginTop: 6 }} title="Estimé à partir des tokens consommés et des prix de la rubrique Paramètres → Modèles Claude">
        {usage.estimatedCostUsd === null ? (
          <span className="muted" style={{ fontSize: 12 }}>Coût estimé : inconnu (prix du modèle non renseigné)</span>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>
            Coût estimé : {formatCostUsd(usage.estimatedCostUsd)}
          </span>
        )}
      </div>
      <button
        className="secondary"
        style={{ width: '100%', marginTop: 8, fontSize: 12, padding: '4px 8px' }}
        onClick={onShowHistory}
      >
        Historique des requêtes
      </button>
    </>
  );
}

// Les menus contextuels, modales et le panneau de résumé IA — treize `{state && <X/>}` indépendants
// dans RepoViewPage à l'origine (aucune imbrication entre eux, mais chacun compte quand même contre
// sa complexité). Regroupés ici pour que ces gates comptent contre CE composant, pas RepoViewPage —
// même logique, mêmes callbacks, juste déplacés.
interface RepoViewModalsProps {
  repoId: string;
  graph: GraphDTO;
  canMarkRangeEnd: boolean;
  canCreateBranch: boolean;
  canCreateTag: boolean;
  canDeleteTag: boolean;
  canDeleteBranch: boolean;
  canEditBranchColors: boolean;
  aiEnabled: boolean;
  branchColorRules: BranchColorRule[];

  menu: ContextMenuPosition | null;
  setMenu: (v: ContextMenuPosition | null) => void;
  rangeStart: string | null;
  setRangeStart: (v: string | null) => void;
  setRangeEnd: (v: string | null) => void;

  tagMenu: TagContextMenuPosition | null;
  setTagMenu: (v: TagContextMenuPosition | null) => void;
  deleteTag: UseMutationResult<void, Error, string>;

  branchMenu: BranchMenuPosition | null;
  setBranchMenu: (v: BranchMenuPosition | null) => void;
  deleteBranch: UseMutationResult<void, Error, string>;

  cherryPickSource: string | null;
  setCherryPickSource: (v: string | null) => void;
  createBranchSource: string | null;
  setCreateBranchSource: (v: string | null) => void;
  createTagSource: string | null;
  setCreateTagSource: (v: string | null) => void;
  branchColorRulesOpen: boolean;
  setBranchColorRulesOpen: (v: boolean) => void;

  summarizeCommit: UseMutationResult<SummaryResponse, Error, string>;
  summarizeRange: UseMutationResult<SummaryResponse, Error, { from: string; to: string }>;
  summaryTitle: string | null;
  setSummaryTitle: (v: string | null) => void;
  summaryData: SummaryResponse | null;
  setSummaryData: (v: SummaryResponse | null) => void;

  setSelected: (fn: (prev: Set<string>) => Set<string>) => void;

  onRefetchGraph: () => void;
  showAiHistory: boolean;
  setShowAiHistory: (v: boolean) => void;
  setFocusedCommitSha: (sha: string | undefined) => void;
}

function RepoViewModals({
  repoId, graph, canMarkRangeEnd, canCreateBranch, canCreateTag, canDeleteTag, canDeleteBranch,
  canEditBranchColors, aiEnabled, branchColorRules,
  menu, setMenu, rangeStart, setRangeStart, setRangeEnd,
  tagMenu, setTagMenu, deleteTag,
  branchMenu, setBranchMenu, deleteBranch,
  cherryPickSource, setCherryPickSource, createBranchSource, setCreateBranchSource,
  createTagSource, setCreateTagSource, branchColorRulesOpen, setBranchColorRulesOpen,
  summarizeCommit, summarizeRange, summaryTitle, setSummaryTitle, summaryData, setSummaryData,
  setSelected, onRefetchGraph, showAiHistory, setShowAiHistory, setFocusedCommitSha,
}: RepoViewModalsProps) {
  return (
    <>
      {menu && (
        <CommitContextMenu
          position={menu}
          onClose={() => setMenu(null)}
          rangeStartSha={rangeStart}
          canMarkRangeEnd={canMarkRangeEnd}
          canCreateBranch={canCreateBranch}
          canCreateTag={canCreateTag}
          aiEnabled={aiEnabled}
          onSummarize={() => {
            setSummaryTitle(`Résumé du commit ${menu.sha.slice(0, 8)}`);
            setSummaryData(null);
            summarizeCommit.reset();
            summarizeCommit.mutate(menu.sha);
            setMenu(null);
          }}
          onStartCherryPick={() => {
            setCherryPickSource(menu.sha);
            setMenu(null);
          }}
          onStartCreateBranch={() => {
            setCreateBranchSource(menu.sha);
            setMenu(null);
          }}
          onStartCreateTag={() => {
            setCreateTagSource(menu.sha);
            setMenu(null);
          }}
          onMarkRangeStart={() => {
            setRangeStart(menu.sha);
            setMenu(null);
          }}
          onCancelRangeStart={() => {
            setRangeStart(null);
            setMenu(null);
          }}
          onMarkRangeEnd={() => {
            if (rangeStart) {
              setRangeEnd(menu.sha);
              setSummaryTitle(`Résumé du range ${rangeStart.slice(0, 8)}..${menu.sha.slice(0, 8)}`);
              setSummaryData(null);
              summarizeRange.reset();
              summarizeRange.mutate({ from: rangeStart, to: menu.sha });
            }
            setMenu(null);
          }}
        />
      )}

      {tagMenu && (
        <TagContextMenu
          position={tagMenu}
          onClose={() => setTagMenu(null)}
          onDelete={() => deleteTag.mutate(tagMenu.tagName)}
          isDeleting={deleteTag.isPending}
          error={deleteTag.isError ? (deleteTag.error as Error).message : null}
          canDelete={canDeleteTag}
        />
      )}

      {branchMenu && (
        <BranchContextMenu
          // Sans key, cliquer directement sur l'arête/tip d'une AUTRE branche pendant qu'un menu
          // est déjà ouvert réutilise la même instance (position.name change, mais le composant ne
          // démonte/remonte pas) : le mousedown qui ferme (BranchContextMenu.tsx) et le click qui
          // rouvre sur la nouvelle branche (GitGraph.tsx) sont deux listeners DOM natifs déclenchés
          // par le même geste, coalescés par le batching de React 18 — branchMenu ne repasse donc
          // jamais par null, `confirmingDelete` fuite d'une branche à l'autre, et le pipeline/Jira
          // affichés restent un instant ceux de l'ancienne branche le temps que les deux useQuery
          // (déjà correctement scopés par position.name) resynchronisent leur JSX. Une key force un
          // remount propre — même pattern que MrTooltip/CherryPickTooltip juste au-dessus.
          key={branchMenu.name}
          repoId={repoId}
          position={branchMenu}
          onClose={() => setBranchMenu(null)}
          onDeleteBranch={() => deleteBranch.mutate(branchMenu.name)}
          isDeletingBranch={deleteBranch.isPending}
          deleteBranchError={deleteBranch.isError ? (deleteBranch.error as Error).message : null}
          canDeleteBranch={canDeleteBranch}
        />
      )}

      {cherryPickSource && (
        <CherryPickModal
          repoId={repoId}
          sourceSha={cherryPickSource}
          onClose={() => {
            setCherryPickSource(null);
            onRefetchGraph();
          }}
        />
      )}

      {createBranchSource && (
        <CreateBranchModal
          repoId={repoId}
          sourceSha={createBranchSource}
          onClose={() => {
            setCreateBranchSource(null);
            onRefetchGraph();
          }}
          onCreated={(branchName) => {
            setSelected((prev) => new Set(prev).add(branchName));
          }}
        />
      )}

      {createTagSource && (
        <CreateTagModal
          repoId={repoId}
          sourceSha={createTagSource}
          onClose={() => {
            setCreateTagSource(null);
            onRefetchGraph();
          }}
        />
      )}

      {branchColorRulesOpen && (
        <Suspense fallback={null}>
          <BranchColorRulesModal
            repoId={repoId}
            rules={branchColorRules}
            branchNames={graph.branches.map((b) => b.name)}
            canEdit={canEditBranchColors}
            onClose={() => setBranchColorRulesOpen(false)}
          />
        </Suspense>
      )}

      {summaryTitle && (summaryData || summarizeCommit.isPending || summarizeRange.isPending || summarizeCommit.isError || summarizeRange.isError) && (
        <Suspense fallback={null}>
          <SummaryPanel
            title={summaryTitle}
            loading={summarizeCommit.isPending || summarizeRange.isPending}
            error={(summarizeCommit.error ?? summarizeRange.error) as Error | null}
            data={summaryData}
            onClose={() => {
              setSummaryData(null);
              setSummaryTitle(null);
              setRangeStart(null);
              setRangeEnd(null);
              summarizeCommit.reset();
              summarizeRange.reset();
            }}
          />
        </Suspense>
      )}

      {showAiHistory && (
        <Suspense fallback={null}>
          <AiHistoryModal
            onClose={() => setShowAiHistory(false)}
            onSelectSha={(sha) => {
              setFocusedCommitSha(sha);
              setShowAiHistory(false);
            }}
          />
        </Suspense>
      )}
    </>
  );
}

export function RepoViewPage() {
  const { id } = useParams();
  const repoId = id!;
  const queryClient = useQueryClient();

  // Sans staleTime, c'était la seule requête de la page à retomber sur le défaut global
  // (staleTime: 0, voir main.tsx) — un aller-retour rapide entre deux repos, ou revenir sur le
  // même après une navigation courte, refaisait systématiquement le fetch le plus coûteux de la
  // page (commits + branches + tags). 30s : plus court que mrQuery/usageQuery (60s) parce que le
  // contenu du graphe est ce que l'utilisateur regarde le plus directement — la fraîcheur y
  // compte davantage que pour un badge de coût IA ou une liste de MR.
  // refetchInterval aligné sur ce même staleTime — même pattern que activePipelinesQuery
  // (GitGraph.tsx). Un push GitLab pendant qu'un onglet reste ouvert sur ce repo n'apparaissait
  // sinon jamais tant que personne ne clique "Rafraîchir" ou ne quitte/revient sur le repo. Le
  // coût par tick reste borné même si rien n'a changé : fetchRepositoryGraph (gitlab.ts) compare
  // toujours last_activity_at avant de reconstruire quoi que ce soit, donc un tick sans nouveau
  // commit ne coûte qu'un seul appel léger à GitLab, pas un refetch complet.
  const graphQuery = useQuery({
    queryKey: ['graph', repoId],
    queryFn: () => api.graph(repoId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const repoMetaQuery = useQuery({
    queryKey: ['repo-meta', repoId],
    queryFn: () => api.getRepo(repoId),
  });
  // GitLab exige au moins le rôle Developer pour créer une branche, créer ou supprimer un tag —
  // si le rôle est inconnu (cache d'accès pas encore chargé, accès révoqué), on n'affiche
  // pas de blocage client : GitLab tranchera de toute façon via le 403 à l'appel.
  const hasWriteAccess = repoMetaQuery.data?.role
    ? ['developer', 'maintainer', 'owner'].includes(repoMetaQuery.data.role)
    : true;
  const canCreateBranch = hasWriteAccess;
  const canCreateTag = hasWriteAccess;
  const canDeleteTag = hasWriteAccess;
  const canDeleteBranch = hasWriteAccess;
  // Rôle plus élevé que les actions Git ci-dessus (maintainer+, pas developer) : ces règles vivent
  // dans notre propre base (Repository.branchColorRules), pas dans GitLab — rien ne les rattrape
  // côté GitLab si un developer les écrase par erreur, contrairement à une branche/un tag. D'où le
  // défaut fermé (false) tant que le rôle n'a pas résolu, contrairement à hasWriteAccess ci-dessus :
  // là il n'y a aucun filet de sécurité côté serveur externe pour rattraper un accès affiché à tort
  // pendant le chargement.
  // Aucune des deux pages SRS/SDD associée : le bouton Traçabilité le signale (pastille) tant que
  // la vue n'a rien à lire — pas bloquant, la modal permet justement de les configurer.
  const traceabilityConfigured = repoMetaQuery.data
    ? Boolean(repoMetaQuery.data.srsPageId || repoMetaQuery.data.sddPageId)
    : true; // pas de pastille « non configuré » le temps que les métadonnées chargent
  const canEditBranchColors = repoMetaQuery.data?.role
    ? ['maintainer', 'owner'].includes(repoMetaQuery.data.role)
    : false;

  const sessionQuery = useQuery({
    queryKey: ['session'],
    queryFn: () => api.getSession().catch(() => null),
    staleTime: Infinity,
  });
  const aiEnabled = sessionQuery.data?.aiEnabled ?? false;

  // Préférences par compte — même clé de cache ['preferences'] que SettingsModal.tsx (thème) :
  // un PATCH depuis l'un des deux composants met à jour le cache partagé, l'autre le voit sans
  // requête supplémentaire.
  const preferencesQuery = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.getPreferences(),
  });

  // Mémorise ce repo comme dernier ouvert, pour qu'App.tsx puisse y rediriger à la reconnexion
  // (voir plus bas dans App.tsx) — un seul PATCH par repo réellement visité, pas à chaque render :
  // on attend que preferencesQuery ait résolu pour comparer, sinon on enverrait un PATCH avant
  // même de savoir si c'est déjà la valeur stockée.
  const lastRepoMutation = useMutation({
    mutationFn: (id: string) => api.updatePreferences({ lastRepoId: id }),
    onSuccess: (updated) => queryClient.setQueryData(['preferences'], updated),
  });
  useEffect(() => {
    if (!preferencesQuery.data) return;
    if (preferencesQuery.data.lastRepoId === repoId) return;
    lastRepoMutation.mutate(repoId);
  }, [repoId, preferencesQuery.data, lastRepoMutation]);

  // Position/zoom du graphe pour CE repo — undefined tant que preferencesQuery n'a pas résolu
  // (GitGraph attend plutôt que de fit-to-view puis se corriger), null si résolu mais qu'aucune
  // position n'a encore été enregistrée pour CE repo précisément. Indexé par repoId
  // (graphViewsByRepoId, packages/shared/src/preferences.ts) plutôt qu'un slot unique partagé par
  // tous les repos : un slot unique (version initiale de ce champ, gael-core-ui 2026-09-10)
  // écrasait la position de tout autre repo dès qu'on scrollait ailleurs, donnant l'impression que
  // la persistance ne marchait pour aucun repo dès qu'on passait de l'un à l'autre — ce qui est le
  // workflow normal (comparer deux repos ouverts tour à tour), pas un cas limite. Séparément de ça,
  // ce champ n'est délibérément PAS gaté sur lastRepoId (mis à jour par sa propre mutation
  // asynchrone juste en dessous) : comparer contre un champ qui peut encore pointer vers le repo
  // précédent le temps que cette mutation finisse son aller-retour créait une course, verrouillant
  // à tort un fit-to-view avant que la correction n'arrive.
  const initialGraphView = preferencesQuery.data === undefined
    ? undefined
    : (preferencesQuery.data.graphViewsByRepoId?.[repoId] ?? null);

  const graphViewMutation = useMutation({
    mutationFn: (view: { scrollLeft: number; scrollTop: number; scale: number }) =>
      api.updatePreferences({ graphViewsByRepoId: { [repoId]: view } }),
    onSuccess: (updated) => queryClient.setQueryData(['preferences'], updated),
  });
  const handleGraphViewChange = useCallback(
    (view: { scrollLeft: number; scrollTop: number; scale: number }) => graphViewMutation.mutate(view),
    [graphViewMutation],
  );

  const [visibleSections, setVisibleSections] = useState<Set<SidebarSectionKey>>(new Set(SIDEBAR_SECTION_KEYS));
  // Bloque la sync tant qu'un toggle vient d'être fait mais pas encore confirmé par le serveur —
  // sinon un refetch en arrière-plan (focus fenêtre) pourrait renvoyer la valeur pré-toggle et
  // faire clignoter la case qu'on vient de cocher/décocher (même principe que dirty/themeDirty
  // dans SettingsModal.tsx).
  const [sectionsDirty, setSectionsDirty] = useState(false);
  useEffect(() => {
    if (!sectionsDirty && preferencesQuery.data?.sidebarSections !== undefined) {
      setVisibleSections(new Set(preferencesQuery.data.sidebarSections));
    }
  }, [preferencesQuery.data, sectionsDirty]);

  const sectionsMutation = useMutation({
    mutationFn: (sections: SidebarSectionKey[]) => api.updatePreferences({ sidebarSections: sections }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['preferences'], updated);
      setSectionsDirty(false);
    },
  });

  const toggleSidebarSection = useCallback((key: string) => {
    const next = new Set(visibleSections);
    if (next.has(key as SidebarSectionKey)) next.delete(key as SidebarSectionKey);
    else next.add(key as SidebarSectionKey);
    setVisibleSections(next);
    setSectionsDirty(true);
    sectionsMutation.mutate([...next]);
  }, [visibleSections, sectionsMutation]);
  // Gate de la rubrique "Tickets Jira" — pas seulement la présence du token (hasJira côté
  // session), mais un vrai appel Jira (BROWSE_PROJECTS) pour vérifier que le token, s'il existe,
  // donne au moins de quoi consulter des tickets. Tant que ça n'a pas résolu, traité comme
  // indisponible (fail-closed, voir resolveJiraAccess côté service) : pas de flash "section
  // visible" qui disparaît une fois la réponse arrivée.
  const jiraAccessQuery = useQuery({
    queryKey: ['jira-access'],
    queryFn: () => api.jiraAccess(),
    staleTime: 60_000,
  });
  const jiraAccess = jiraAccessQuery.data?.hasToken === true && jiraAccessQuery.data.canBrowse === true;
  const jiraDisabledReason = jiraAccessQuery.data?.hasToken === false
    ? 'Configure un token Jira dans les Paramètres'
    : jiraAccessQuery.data && !jiraAccessQuery.data.canBrowse
      ? "Ce token Jira n'a pas le droit de consulter les tickets (BROWSE_PROJECTS refusé)"
      : undefined;

  const sidebarSectionOptions: SidebarSectionOption[] = [
    { key: 'branches', label: 'Branches' },
    { key: 'mergeRequests', label: 'Merge Requests' },
    { key: 'cherryPicks', label: 'Cherry-picks' },
    { key: 'jiraTickets', label: 'Tickets Jira', disabled: !jiraAccess, disabledReason: jiraDisabledReason },
    ...(aiEnabled ? [{ key: 'claude', label: 'Conso. Claude' }] : []),
  ];

  const usageQuery = useQuery({
    queryKey: ['ai-usage'],
    queryFn: () => api.aiUsage(),
    staleTime: 60_000,
  });

  const mrQuery = useQuery({
    queryKey: ['merge-requests', repoId],
    queryFn: () => api.mergeRequests(repoId),
    staleTime: 60_000,
  });

  // Dépend de graph.branches, seulement connu une fois graphQuery résolue — `enabled` plutôt
  // qu'un retour anticipé, cette query doit rester déclarée avant les `if (...) return null`
  // plus bas (règle des Hooks : ordre d'appel stable à chaque rendu).
  const branchNamesForTickets = graphQuery.data?.branches.map((b) => b.name) ?? [];
  const jiraTicketsQuery = useQuery({
    queryKey: ['jira-repo-tickets', repoId, branchNamesForTickets],
    queryFn: () => api.repoTickets(repoId, branchNamesForTickets),
    enabled: graphQuery.data !== undefined && jiraAccess,
    staleTime: 60_000,
  });

  const { collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed } = useSidebar();
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const dragging = useRef(false);

  // Largeur et repli de la sidebar — même préférence par compte que le reste ci-dessus, mêmes
  // gardes *Dirty pour ne pas se faire écraser par un refetch en arrière-plan avant confirmation
  // serveur. Deux mutations distinctes (pas une seule partagée) : chacune ne doit réinitialiser
  // que son propre flag dirty au retour, sans dépendre de laquelle des deux a été appelée.
  const [sidebarWidthDirty, setSidebarWidthDirty] = useState(false);
  useEffect(() => {
    if (!sidebarWidthDirty && preferencesQuery.data?.sidebarWidth !== undefined) {
      setSidebarWidth(preferencesQuery.data.sidebarWidth);
    }
  }, [preferencesQuery.data, sidebarWidthDirty]);

  const sidebarWidthMutation = useMutation({
    mutationFn: (width: number) => api.updatePreferences({ sidebarWidth: width }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['preferences'], updated);
      setSidebarWidthDirty(false);
    },
  });

  const [sidebarCollapsedDirty, setSidebarCollapsedDirty] = useState(false);
  useEffect(() => {
    if (!sidebarCollapsedDirty && preferencesQuery.data?.sidebarCollapsed !== undefined) {
      setSidebarCollapsed(preferencesQuery.data.sidebarCollapsed);
    }
  }, [preferencesQuery.data, sidebarCollapsedDirty, setSidebarCollapsed]);

  const sidebarCollapsedMutation = useMutation({
    mutationFn: (collapsed: boolean) => api.updatePreferences({ sidebarCollapsed: collapsed }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['preferences'], updated);
      setSidebarCollapsedDirty(false);
    },
  });

  const handleToggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    setSidebarCollapsedDirty(true);
    sidebarCollapsedMutation.mutate(next);
  };

  const onResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX;
    const startW = sidebarWidth;
    // Même principe que sectionWeights : poids/valeur final(e) tenu(e) à jour à chaque
    // mousemove dans une variable locale, persisté(e) une seule fois au mouseup.
    let finalWidth = sidebarWidth;
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      finalWidth = Math.max(200, Math.min(560, startW + ev.clientX - startX));
      setSidebarWidth(finalWidth);
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (finalWidth !== startW) {
        setSidebarWidthDirty(true);
        sidebarWidthMutation.mutate(finalWidth);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // open/weight pilotent un split-view façon VS Code entre les rubriques de la sidebar (voir
  // resolveSectionResize) : `open` correspond au chevron plier/déplier de chaque rubrique
  // (repris ici depuis SidebarSection pour que le drag puisse aussi replier une rubrique), et
  // `weight` est son flex-grow/shrink — en unités de pixels dès qu'un drag a eu lieu (voir
  // onSectionResizeMouseDown), sinon les poids par défaut ci-dessus.
  // Seule Branches est dépliée par défaut. mergeRequests/cherryPicks sont ensuite corrigées une
  // fois pour toutes par les effets d'auto-ouverture plus bas dès que leur requête résout (open
  // si le repo en a au moins un) ; claude n'a pas un tel effet, donc son défaut fermé est
  // définitif tant que l'utilisateur ne la déplie pas lui-même (auquel cas ça se persiste, voir
  // toggleOpenSection ci-dessous).
  const [openSections, setOpenSections] = useState<Record<SidebarSectionKey, boolean>>({
    branches: true,
    mergeRequests: false,
    cherryPicks: false,
    jiraTickets: false,
    claude: false,
  });
  const [mrInitialized, setMrInitialized] = useState(false);
  const [mrTooltip, setMrTooltip] = useState<
    { mr: MergeRequestDTO; anchorX: number; rowTop: number; rowBottom: number } | null
  >(null);
  const mrTooltipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [cpTooltip, setCpTooltip] = useState<
    // readOnly : survol depuis un lien du graphe (pas la sidebar) — pas de note éditable ni de
    // suppression à ce niveau, ces actions restent réservées à la liste "Cherry-Picks" de la
    // sidebar, l'endroit pensé pour ça (confirmation de suppression, feedback d'enregistrement).
    { cp: CherryPickEdgeDTO; anchorX: number; rowTop: number; rowBottom: number; readOnly?: boolean } | null
  >(null);
  const cpTooltipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [jiraTicketTooltip, setJiraTicketTooltip] = useState<
    { ticket: JiraTooltipTarget; anchorX: number; rowTop: number; rowBottom: number } | null
  >(null);
  const jiraTicketTooltipHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const [sectionWeights, setSectionWeights] = useState<Record<SidebarSectionKey, number>>(SECTION_DEFAULT_WEIGHT);
  const sectionElRefs = useRef<Partial<Record<SidebarSectionKey, HTMLDivElement>>>({});

  // Même préférence par compte que visibleSections ci-dessus, même garde weightsDirty pour la
  // même raison (ne pas laisser un refetch en arrière-plan écraser un drag tout juste terminé
  // avant que le PATCH n'ait confirmé). Le merge avec SECTION_DEFAULT_WEIGHT couvre le cas d'une
  // rubrique jamais redimensionnée (absente de la préférence stockée) ou ajoutée après coup.
  const [weightsDirty, setWeightsDirty] = useState(false);
  useEffect(() => {
    if (!weightsDirty && preferencesQuery.data?.sectionWeights) {
      setSectionWeights({ ...SECTION_DEFAULT_WEIGHT, ...preferencesQuery.data.sectionWeights });
    }
  }, [preferencesQuery.data, weightsDirty]);

  const sectionWeightsMutation = useMutation({
    mutationFn: (weights: Record<SidebarSectionKey, number>) => api.updatePreferences({ sectionWeights: weights }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['preferences'], updated);
      setWeightsDirty(false);
    },
  });

  // Plié/déplié — toute clé présente dans la préférence chargée est appliquée (voir
  // PERSISTABLE_OPEN_KEYS ci-dessus). Pour cherryPicks/mergeRequests, ça prime sur les effets
  // d'auto-ouverture plus bas, qui ne s'appliquent eux-mêmes que si la clé est absente ici.
  const [openSectionsDirty, setOpenSectionsDirty] = useState(false);
  useEffect(() => {
    if (!openSectionsDirty && preferencesQuery.data?.openSections) {
      setOpenSections((prev) => {
        const next = { ...prev };
        for (const k of PERSISTABLE_OPEN_KEYS) {
          const stored = preferencesQuery.data.openSections?.[k];
          if (stored !== undefined) next[k] = stored;
        }
        return next;
      });
    }
  }, [preferencesQuery.data, openSectionsDirty]);

  const openSectionsMutation = useMutation({
    // N'envoie que la/les clé(s) réellement changée(s), jamais l'état local complet — le serveur
    // merge maintenant openSections clé par clé (voir routes/preferences.ts), donc plier
    // `mergeRequests` ne fige plus au passage la valeur auto-calculée de `cherryPicks`, ni
    // n'écrase une préférence déjà enregistrée pour `branches`.
    mutationFn: (open: Partial<Record<SidebarSectionKey, boolean>>) => api.updatePreferences({ openSections: open }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['preferences'], updated);
      setOpenSectionsDirty(false);
    },
  });

  const toggleOpenSection = useCallback((key: SidebarSectionKey) => {
    const value = !openSections[key];
    setOpenSections((prev) => ({ ...prev, [key]: value }));
    setOpenSectionsDirty(true);
    openSectionsMutation.mutate({ [key]: value });
  }, [openSections, openSectionsMutation]);

  // Plié/déplié des sous-groupes du filtre de branches (BranchFilter.tsx) — même pattern que
  // openSections ci-dessus, mais clés dynamiques ('rule:N' selon les règles de couleur du repo) :
  // le merge se fait donc directement sur les clés présentes dans la préférence stockée plutôt que
  // sur une liste fixe (PERSISTABLE_OPEN_KEYS), en partant du défaut de BranchFilter pour toute clé
  // jamais réglée.
  const [collapsedBranchGroups, setCollapsedBranchGroups] = useState<Set<string>>(
    () => new Set(DEFAULT_COLLAPSED_BRANCH_GROUPS),
  );
  const [collapsedBranchGroupsDirty, setCollapsedBranchGroupsDirty] = useState(false);
  useEffect(() => {
    if (!collapsedBranchGroupsDirty && preferencesQuery.data?.collapsedBranchGroups) {
      const stored = preferencesQuery.data.collapsedBranchGroups;
      setCollapsedBranchGroups((prev) => {
        const next = new Set(prev);
        for (const [k, v] of Object.entries(stored)) {
          if (v) next.add(k); else next.delete(k);
        }
        return next;
      });
    }
  }, [preferencesQuery.data, collapsedBranchGroupsDirty]);

  const collapsedBranchGroupsMutation = useMutation({
    mutationFn: (patch: Record<string, boolean>) => api.updatePreferences({ collapsedBranchGroups: patch }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['preferences'], updated);
      setCollapsedBranchGroupsDirty(false);
    },
  });

  const toggleBranchGroupCollapse = useCallback((group: string) => {
    const value = !collapsedBranchGroups.has(group);
    setCollapsedBranchGroups((prev) => {
      const next = new Set(prev);
      if (value) next.add(group); else next.delete(group);
      return next;
    });
    setCollapsedBranchGroupsDirty(true);
    collapsedBranchGroupsMutation.mutate({ [group]: value });
  }, [collapsedBranchGroups, collapsedBranchGroupsMutation]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const [menu, setMenu] = useState<ContextMenuPosition | null>(null);
  const [tagMenu, setTagMenu] = useState<TagContextMenuPosition | null>(null);
  const [branchMenu, setBranchMenu] = useState<BranchMenuPosition | null>(null);
  const [cherryPickSource, setCherryPickSource] = useState<string | null>(null);
  const [createBranchSource, setCreateBranchSource] = useState<string | null>(null);
  const [createTagSource, setCreateTagSource] = useState<string | null>(null);
  const [branchColorRulesOpen, setBranchColorRulesOpen] = useState(false);
  const [traceabilityOpen, setTraceabilityOpen] = useState(false);
  const [ciConfigOpen, setCiConfigOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = useState<string | null>(null);

  const commitMap = useMemo(
    () => new Map((graphQuery.data?.commits ?? []).map((c) => [c.sha, c])),
    [graphQuery.data?.commits],
  );


  // True iff the menu's SHA is a strict first-parent descendant of rangeStart
  const canMarkRangeEnd = useMemo(() => {
    if (!rangeStart || !menu) return false;
    if (menu.sha === rangeStart) return false;
    let cur = menu.sha;
    for (let i = 0; i < 10_000; i++) {
      const c = commitMap.get(cur);
      if (!c || c.parents.length === 0) return false;
      const p = c.parents[0];
      if (p === rangeStart) return true;
      cur = p;
    }
    return false;
  }, [rangeStart, menu, commitMap]);

  const [selectedCherryPickId, setSelectedCherryPickId] = useState<string | null>(null);
  const [focusedCommitSha, setFocusedCommitSha] = useState<string | undefined>(undefined);
  // Clic sur un item de BranchFilter.tsx (hors case à cocher) — voir l'effet dédié dans
  // GitGraph.tsx, même convention que focusedCommitSha ci-dessus.
  const [focusedBranchName, setFocusedBranchName] = useState<string | undefined>(undefined);
  const [visibleShas, setVisibleShas] = useState<Set<string>>(new Set());
  const handleVisibleShas = useCallback((shas: Set<string>) => setVisibleShas(shas), []);
  const [summaryTitle, setSummaryTitle] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<SummaryResponse | null>(null);
  const [showAiHistory, setShowAiHistory] = useState(false);

  // Branches déjà vues pour ce dépôt : une branche créée hors de Clearflow (push d'un collègue,
  // GitLab directement) apparaît dans graph.branches après un rafraîchissement mais n'était pas
  // dans `selected` (initialisé une seule fois) — buildGitgraphModel la filtrait alors, et seul un
  // rechargement de la page l'affichait. On sélectionne donc toute branche *nouvelle* au fil des
  // refetch, sans jamais re-cocher une branche que l'utilisateur a décochée (déjà connue).
  const knownBranchesRef = useRef<{ repoId: string; names: Set<string> }>({ repoId, names: new Set() });
  useEffect(() => {
    if (!graphQuery.data) return;
    const names = computeBranchEntries(graphQuery.data).map((b) => b.name);
    if (knownBranchesRef.current.repoId !== repoId) knownBranchesRef.current = { repoId, names: new Set() };
    const known = knownBranchesRef.current.names;
    const fresh = names.filter((n) => !known.has(n));
    fresh.forEach((n) => known.add(n));
    if (!initialized) {
      setSelected(new Set(names));
      setInitialized(true);
    } else if (fresh.length > 0) {
      setSelected((prev) => new Set([...prev, ...fresh]));
    }
  }, [graphQuery.data, initialized, repoId]);

  // Flag séparé de `initialized` ci-dessus : cette décision attend en plus preferencesQuery,
  // pour ne jamais appliquer l'auto-ouverture puis aussitôt la corriger (flash) — et surtout ne
  // jamais l'appliquer du tout si l'utilisateur a déjà une préférence enregistrée pour
  // cherryPicks (voir PERSISTABLE_OPEN_KEYS/toggleOpenSection : un repli manuel devient
  // définitif, cette auto-ouverture ne joue plus que tant qu'il n'a jamais touché la rubrique).
  const [cherryPicksAutoOpenApplied, setCherryPicksAutoOpenApplied] = useState(false);
  useEffect(() => {
    if (graphQuery.data && preferencesQuery.data && !cherryPicksAutoOpenApplied) {
      if (preferencesQuery.data.openSections?.cherryPicks === undefined) {
        setOpenSections((prev) => ({ ...prev, cherryPicks: graphQuery.data!.cherryPicks.length > 0 }));
      }
      setCherryPicksAutoOpenApplied(true);
    }
  }, [graphQuery.data, preferencesQuery.data, cherryPicksAutoOpenApplied]);

  // Query séparée de graphQuery (source différente) : effet dédié plutôt que casé dans celui
  // du dessus, pour ne pas dépendre d'un ordre d'arrivée particulier entre les deux. Même
  // condition sur preferencesQuery que ci-dessus.
  useEffect(() => {
    if (mrQuery.data && !mrInitialized && preferencesQuery.data) {
      if (preferencesQuery.data.openSections?.mergeRequests === undefined) {
        setOpenSections((prev) => ({ ...prev, mergeRequests: mrQuery.data!.length > 0 }));
      }
      setMrInitialized(true);
    }
  }, [mrQuery.data, mrInitialized, preferencesQuery.data]);

  const summarizeCommit = useMutation({
    mutationFn: (sha: string) => api.summarizeCommit({ repoId, sha }),
    onSuccess: (data) => {
      setSummaryData(data);
      queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
      queryClient.invalidateQueries({ queryKey: ['summarized-shas', repoId] });
    },
  });

  const summarizeRange = useMutation({
    mutationFn: (vars: { from: string; to: string }) =>
      api.summarizeRange({ repoId, fromSha: vars.from, toSha: vars.to }),
    onSuccess: (data) => {
      setSummaryData(data);
      queryClient.invalidateQueries({ queryKey: ['ai-usage'] });
    },
  });

  const deleteTag = useMutation({
    mutationFn: (tagName: string) => api.deleteTag(repoId, tagName),
    onSuccess: () => {
      setTagMenu(null);
      queryClient.invalidateQueries({ queryKey: ['graph', repoId] });
    },
  });

  const deleteBranch = useMutation({
    mutationFn: (branchName: string) => api.deleteBranch(repoId, branchName),
    // Mise à jour optimiste avant l'invalidation : le rebuild complet déclenché par
    // invalidateQueries refait un Commits.all par branche restante contre le vrai GitLab
    // (~10s mesurés sur ce repo de test, cf. gitlab.ts) — sans ça, la pill de la branche
    // supprimée reste affichée pendant tout ce temps alors que la suppression a déjà réussi.
    // setQueryData retire juste la branche de la liste ; buildGitgraphModel ne calcule les
    // commits atteignables qu'à partir de branchHeads (dérivé de cette liste), donc les
    // commits qui n'appartenaient qu'à elle disparaissent aussi immédiatement, sans qu'il soit
    // nécessaire de toucher au tableau `commits`. invalidateQueries reste nécessaire ensuite
    // pour resynchroniser avec l'état exact de GitLab (tags, autres branches modifiées entre
    // temps, etc.) une fois le rebuild terminé.
    onSuccess: (_data, branchName) => {
      setBranchMenu(null);
      queryClient.setQueryData<GraphDTO>(['graph', repoId], (old) =>
        old ? { ...old, branches: old.branches.filter((b) => b.name !== branchName) } : old,
      );
      queryClient.invalidateQueries({ queryKey: ['graph', repoId] });
    },
  });

  if (graphQuery.isLoading) return (
    <div className="row" style={{ padding: '12px 0' }}>
      <span className="spinning" aria-hidden="true">↻</span>
      <p className="muted">Chargement du dépôt…</p>
    </div>
  );
  if (graphQuery.isError)
    return <span className="badge error">Erreur : {(graphQuery.error as Error).message}</span>;
  if (!graphQuery.data) return null;
  const graph = graphQuery.data;

  const branchEntries = computeBranchEntries(graph);
  const primaryBranchOf = computePrimaryBranch(graph, branchEntries);
  const branchChildren = computeBranchChildren(graph, branchEntries, primaryBranchOf);

  const claudeVisible = aiEnabled && !!usageQuery.data && visibleSections.has('claude');
  // Même principe que claudeVisible : la rubrique n'est effectivement affichée que si en plus
  // d'être cochée dans le menu, l'accès Jira le permet (voir jiraAccess plus haut) — le menu la
  // montre grisée dans le cas contraire, mais son corps ne se rend jamais.
  const jiraTicketsVisible = jiraAccess && visibleSections.has('jiraTickets');
  const mrs = mrQuery.data ?? [];
  // Ordre DOM fixe des rubriques actuellement affichées (masquées via le menu ☰ exclues) —
  // sert à la fois à savoir si une poignée de resize a un prédécesseur visible au-dessus
  // d'elle, et à construire la chaîne des rubriques ouvertes pour resolveSectionResize.
  const visibleOrder: SidebarSectionKey[] = SIDEBAR_SECTION_KEYS.filter((k) =>
    k === 'claude' ? claudeVisible : k === 'jiraTickets' ? jiraTicketsVisible : visibleSections.has(k),
  );
  const mergeRequestsHasPriorSection = visibleOrder.indexOf('mergeRequests') > 0;
  const cherryPicksHasPriorSection = visibleOrder.indexOf('cherryPicks') > 0;
  const jiraTicketsHasPriorSection = visibleOrder.indexOf('jiraTickets') > 0;
  const claudeHasPriorSection = visibleOrder.indexOf('claude') > 0;

  function sectionFlexStyle(key: SidebarSectionKey): React.CSSProperties {
    if (!openSections[key]) return { flex: '0 0 auto' };
    const weight = sectionWeights[key];
    return { flex: `${weight} ${weight} 0px`, minHeight: SECTION_MIN_HEIGHT[key] };
  }

  function sectionRootRef(key: SidebarSectionKey) {
    return (el: HTMLDivElement | null) => {
      if (el) sectionElRefs.current[key] = el;
      else delete sectionElRefs.current[key];
    };
  }

  // La poignée est au-dessus de `afterCandidateKey` dans l'ordre DOM. Elle agit sur la paire
  // de rubriques OUVERTES la plus proche de chaque côté (une rubriques repliée entre les deux
  // n'a pas de corps à redimensionner, donc on la traverse pour trouver le vrai voisin).
  const onSectionResizeMouseDown = (afterCandidateKey: SidebarSectionKey, e: React.MouseEvent) => {
    e.preventDefault();
    const posInOrder = visibleOrder.indexOf(afterCandidateKey);

    let afterKey: SidebarSectionKey | undefined;
    for (let i = posInOrder; i < visibleOrder.length; i++) {
      if (openSections[visibleOrder[i]]) { afterKey = visibleOrder[i]; break; }
    }
    let beforeKey: SidebarSectionKey | undefined;
    for (let i = posInOrder - 1; i >= 0; i--) {
      if (openSections[visibleOrder[i]]) { beforeKey = visibleOrder[i]; break; }
    }
    if (!beforeKey || !afterKey) return;

    const openChain = visibleOrder.filter((k) => openSections[k]);
    const pivot = openChain.indexOf(afterKey);

    // On fige les hauteurs actuelles de TOUTES les rubriques ouvertes (pas seulement la paire
    // qu'on glisse) en unités de pixels cohérentes, pour que les rubriques non touchées par ce
    // drag gardent exactement leur part actuelle une fois reconverties en poids flex. On mesure
    // la rubrique entière (.sidebar-section, en-tête inclus) — c'est elle qui porte le style
    // flex-grow/shrink, pas .sidebar-section-body : mesurer le corps seul décale tous les
    // calculs de la hauteur constante de l'en-tête.
    const startHeights = openChain.map((k) => sectionElRefs.current[k]?.getBoundingClientRect().height ?? SECTION_MIN_HEIGHT[k]);
    const startY = e.clientY;
    // Poids finaux du drag en cours, tenus à jour à chaque mousemove — lus au mouseup pour
    // persister, plutôt que de relire l'état React sectionWeights (son timing exact d'application
    // n'est pas garanti au moment précis du mouseup). didResize évite un PATCH pour un simple clic
    // sur la poignée sans déplacement réel.
    let finalWeights = sectionWeights;
    // Clés effectivement repliées par CE drag (partiel) — pas l'état local complet, pour la même
    // raison que toggleOpenSection : ne persister que ce que ce geste a réellement changé.
    const collapsedKeys: Partial<Record<SidebarSectionKey, boolean>> = {};
    let didResize = false;

    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientY - startY;
      const { heights, collapse } = resolveSectionResize(openChain, startHeights, pivot, delta);
      didResize = true;
      setSectionWeights((prev) => {
        const next = { ...prev };
        openChain.forEach((k, i) => { next[k] = heights[i]; });
        finalWeights = next;
        return next;
      });
      if (collapse.size > 0) {
        setOpenSections((prev) => {
          const next = { ...prev };
          collapse.forEach((i) => {
            next[openChain[i]] = false;
            collapsedKeys[openChain[i]] = false;
          });
          return next;
        });
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (didResize) {
        setWeightsDirty(true);
        sectionWeightsMutation.mutate(finalWeights);
      }
      if (Object.keys(collapsedKeys).length > 0) {
        setOpenSectionsDirty(true);
        openSectionsMutation.mutate(collapsedKeys);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleMrRowHover = (mr: MergeRequestDTO, rowRect: DOMRect) => {
    if (mrTooltipHideRef.current) clearTimeout(mrTooltipHideRef.current);
    const anchorX = sidebarRef.current?.getBoundingClientRect().right ?? rowRect.right;
    setMrTooltip({ mr, anchorX, rowTop: rowRect.top, rowBottom: rowRect.bottom });
  };
  const handleMrRowLeave = () => {
    mrTooltipHideRef.current = setTimeout(() => setMrTooltip(null), 120);
  };

  const handleCherryPickRowHover = (cp: CherryPickEdgeDTO, rowRect: DOMRect) => {
    if (cpTooltipHideRef.current) clearTimeout(cpTooltipHideRef.current);
    const anchorX = sidebarRef.current?.getBoundingClientRect().right ?? rowRect.right;
    setCpTooltip({ cp, anchorX, rowTop: rowRect.top, rowBottom: rowRect.bottom });
  };
  const handleCherryPickRowLeave = () => {
    cpTooltipHideRef.current = setTimeout(() => setCpTooltip(null), 120);
  };

  const handleJiraTicketHover = (ticket: JiraTooltipTarget, rowRect: DOMRect) => {
    if (jiraTicketTooltipHideRef.current) clearTimeout(jiraTicketTooltipHideRef.current);
    const anchorX = sidebarRef.current?.getBoundingClientRect().right ?? rowRect.right;
    setJiraTicketTooltip({ ticket, anchorX, rowTop: rowRect.top, rowBottom: rowRect.bottom });
  };
  const handleJiraTicketLeave = () => {
    jiraTicketTooltipHideRef.current = setTimeout(() => setJiraTicketTooltip(null), 120);
  };

  return (
    <>
      <div className="repo-view-main">
        <aside
          ref={sidebarRef}
          className="repo-view-sidebar"
          style={sidebarCollapsed ? { display: 'none' } : { width: sidebarWidth }}
        >
          <div className="sidebar-toolbar">
            <SidebarSectionsMenu
              options={sidebarSectionOptions}
              visible={visibleSections}
              onToggle={toggleSidebarSection}
            />
            <button
              type="button"
              className="sidebar-sections-toggle"
              onClick={() => setBranchColorRulesOpen(true)}
              title="Couleurs de branche"
            >
              <PaletteIcon />
            </button>
          </div>

          {visibleSections.has('branches') && (
          <SidebarSection
            title="Branches"
            open={openSections.branches}
            onToggleOpen={() => toggleOpenSection('branches')}
            style={sectionFlexStyle('branches')}
            bodyStyle={{ padding: 0 }}
            rootRef={sectionRootRef('branches')}
          >
            <div className="sidebar-filter-scroll">
              <BranchFilter
                branches={branchEntries}
                commits={graph.commits}
                descendantsOf={branchChildren}
                selected={selected}
                onChange={setSelected}
                onFlyToBranch={setFocusedBranchName}
                colorRules={repoMetaQuery.data?.branchColorRules ?? []}
                collapsedGroups={collapsedBranchGroups}
                onToggleCollapse={toggleBranchGroupCollapse}
              />
            </div>
          </SidebarSection>
          )}

          {visibleSections.has('mergeRequests') && (
            <SidebarResizableSection
              hasPriorSection={mergeRequestsHasPriorSection}
              onResizeMouseDown={(e) => onSectionResizeMouseDown('mergeRequests', e)}
              title={mrQuery.data ? `Merge Requests (${mrs.length})` : 'Merge Requests'}
              open={openSections.mergeRequests}
              onToggleOpen={() => toggleOpenSection('mergeRequests')}
              style={sectionFlexStyle('mergeRequests')}
              rootRef={sectionRootRef('mergeRequests')}
            >
              <MergeRequestsSectionBody
                mrQuery={mrQuery}
                mrs={mrs}
                onHoverRow={handleMrRowHover}
                onLeaveRow={handleMrRowLeave}
              />
            </SidebarResizableSection>
          )}

          {visibleSections.has('cherryPicks') && (
            <SidebarResizableSection
              hasPriorSection={cherryPicksHasPriorSection}
              onResizeMouseDown={(e) => onSectionResizeMouseDown('cherryPicks', e)}
              title={`Cherry-picks (${graph.cherryPicks.length})`}
              open={openSections.cherryPicks}
              onToggleOpen={() => toggleOpenSection('cherryPicks')}
              style={sectionFlexStyle('cherryPicks')}
              rootRef={sectionRootRef('cherryPicks')}
            >
              <CherryPicksSectionBody
                cherryPicks={graph.cherryPicks}
                primaryBranchOf={primaryBranchOf}
                selectedCherryPickId={selectedCherryPickId}
                onSelect={setSelectedCherryPickId}
                onHoverRow={handleCherryPickRowHover}
                onLeaveRow={handleCherryPickRowLeave}
              />
            </SidebarResizableSection>
          )}

          {jiraTicketsVisible && (
            <SidebarResizableSection
              hasPriorSection={jiraTicketsHasPriorSection}
              onResizeMouseDown={(e) => onSectionResizeMouseDown('jiraTickets', e)}
              title={jiraTicketsQuery.data ? `Tickets Jira (${jiraTicketsQuery.data.length})` : 'Tickets Jira'}
              open={openSections.jiraTickets}
              onToggleOpen={() => toggleOpenSection('jiraTickets')}
              style={sectionFlexStyle('jiraTickets')}
              rootRef={sectionRootRef('jiraTickets')}
            >
              <JiraTicketsSectionBody
                jiraTicketsQuery={jiraTicketsQuery}
                tickets={jiraTicketsQuery.data ?? []}
                onHoverTicket={handleJiraTicketHover}
                onLeaveTicket={handleJiraTicketLeave}
              />
            </SidebarResizableSection>
          )}

          {claudeVisible && (
            <SidebarResizableSection
              hasPriorSection={claudeHasPriorSection}
              onResizeMouseDown={(e) => onSectionResizeMouseDown('claude', e)}
              title="Conso. Claude"
              open={openSections.claude}
              onToggleOpen={() => toggleOpenSection('claude')}
              style={sectionFlexStyle('claude')}
              rootRef={sectionRootRef('claude')}
            >
              <ClaudeUsageSectionBody usage={usageQuery.data!} onShowHistory={() => setShowAiHistory(true)} />
            </SidebarResizableSection>
          )}
        </aside>

        {/* Dans l'espace entre sidebar et graphe (le gap de 6px de .repo-view-main), pas nichée
            dans <aside> comme avant : son overflow:hidden aurait découpé tout ce qui dépasse de
            sa propre bordure. Sibling positionné comme .sidebar-collapse-toggle juste en dessous
            (left: sidebarWidth), sans transform de recentrage — la hit-zone démarre pile à la
            bordure de la sidebar et s'étend uniquement vers la droite (voir largeur en CSS), donc
            ne remonte jamais sur le contenu/la scrollbar de la sidebar. Masquée avec elle
            (rien à redimensionner sidebar masquée) — <aside> le faisait implicitement par
            héritage de display:none avant que ce ne soit un sibling. */}
        {!sidebarCollapsed && (
          <div
            className="sidebar-resizer"
            style={{ left: sidebarWidth }}
            onMouseDown={onResizerMouseDown}
          />
        )}

        {/* Ancré sur la bordure de la sidebar (pas dans le header, voir App.tsx) : reste
            accessible qu'elle soit ouverte ou masquée (l'<aside> passe en display:none),
            puisqu'il vit en dehors et se repositionne juste en fonction de sidebarWidth. */}
        <button
          className="sidebar-collapse-toggle"
          style={{ left: sidebarCollapsed ? 0 : sidebarWidth }}
          onClick={handleToggleSidebar}
          title={sidebarCollapsed ? 'Afficher la sidebar' : 'Masquer la sidebar'}
        >
          {sidebarCollapsed ? '▶' : '◀'}
        </button>

        {mrTooltip && (
          <MrTooltip
            key={mrTooltip.mr.iid}
            repoId={repoId}
            mr={mrTooltip.mr}
            anchorX={mrTooltip.anchorX}
            rowTop={mrTooltip.rowTop}
            rowBottom={mrTooltip.rowBottom}
            onMouseEnter={() => { if (mrTooltipHideRef.current) clearTimeout(mrTooltipHideRef.current); }}
            onMouseLeave={() => setMrTooltip(null)}
            onMerged={() => setMrTooltip(null)}
          />
        )}

        {cpTooltip && (
          <CherryPickTooltip
            key={cpTooltip.cp.id}
            cp={cpTooltip.cp}
            sourceBranch={primaryBranchOf.get(cpTooltip.cp.sourceSha)}
            targetBranch={primaryBranchOf.get(cpTooltip.cp.targetSha)}
            anchorX={cpTooltip.anchorX}
            rowTop={cpTooltip.rowTop}
            rowBottom={cpTooltip.rowBottom}
            readOnly={cpTooltip.readOnly}
            onMouseEnter={() => { if (cpTooltipHideRef.current) clearTimeout(cpTooltipHideRef.current); }}
            onMouseLeave={() => setCpTooltip(null)}
            onDeleted={() => {
              if (cpTooltip.cp.id === selectedCherryPickId) setSelectedCherryPickId(null);
              setCpTooltip(null);
              graphQuery.refetch();
            }}
            onNoteUpdated={(note) => {
              setCpTooltip((prev) => (prev ? { ...prev, cp: { ...prev.cp, note } } : prev));
              graphQuery.refetch();
            }}
          />
        )}

        {jiraTicketTooltip && (
          <JiraTicketTooltip
            key={jiraTicketTooltip.ticket.ticketKey}
            ticket={jiraTicketTooltip.ticket}
            repoName={repoMetaQuery.data?.name}
            anchorX={jiraTicketTooltip.anchorX}
            rowTop={jiraTicketTooltip.rowTop}
            rowBottom={jiraTicketTooltip.rowBottom}
            onMouseEnter={() => { if (jiraTicketTooltipHideRef.current) clearTimeout(jiraTicketTooltipHideRef.current); }}
            onMouseLeave={() => setJiraTicketTooltip(null)}
            onFlyToBranch={setFocusedBranchName}
          />
        )}

        <div className="repo-view-content">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <CommitSearchBar
              commits={visibleShas.size > 0 ? graph.commits.filter((c) => visibleShas.has(c.sha)) : graph.commits}
              tags={visibleShas.size > 0 ? graph.tags.filter((t) => visibleShas.has(t.sha)) : graph.tags}
              onSelect={(sha) => setFocusedCommitSha(sha)}
            />
            <div className="row" style={{ gap: 6 }}>
              <button
                type="button"
                className="secondary"
                onClick={() => setTraceabilityOpen(true)}
                title={traceabilityConfigured
                  ? 'Traçabilité SRS / SDD / Jira / commits'
                  : 'Traçabilité — aucune page SRS/SDD associée à ce dépôt (non configuré)'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <TraceabilityIcon />
                Traçabilité
                {!traceabilityConfigured && (
                  <span
                    aria-label="Non configuré"
                    style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }}
                  />
                )}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setCiConfigOpen(true)}
                title="Configuration CI — stages dont les jobs manuels proposent « Lancer »"
              >
                CI
              </button>
              <button
                className="secondary btn-icon"
                onClick={() => graphQuery.refetch()}
                disabled={graphQuery.isFetching}
                title="Rafraîchir le graphe"
              >
                <span className={graphQuery.isFetching ? 'spinning' : ''}>↻</span>
              </button>
            </div>
          </div>

          {/* Attend que `selected` soit initialisé (tous les branches par défaut, voir l'effet
              plus haut) avant de monter GitGraph : sinon son tout premier rendu se ferait avec
              selectedBranches vide, produisant un layout minuscule/transitoire sur lequel le
              fit-to-view/restauration de position (initialFitDone, une seule fois) se figerait —
              le vrai layout arrivant l'instant d'après ne serait alors plus jamais re-fitté, et
              une position restaurée serait clampée à ~0 par un conteneur encore quasi-vide. */}
          {initialized && (
            <GitGraph
              repoId={repoId}
              graph={graph}
              selectedBranches={selected}
              rangeStartSha={rangeStart ?? undefined}
              rangeEndSha={rangeEnd ?? undefined}
              initialView={initialGraphView}
              onViewChange={handleGraphViewChange}
              onCommitContextMenu={(sha, evt) => {
                evt.preventDefault?.();
                setMenu({ x: evt.clientX, y: evt.clientY, sha });
              }}
              onTagContextMenu={(tagName, evt) => {
                evt.preventDefault?.();
                deleteTag.reset();
                setTagMenu({ x: evt.clientX, y: evt.clientY, tagName });
              }}
              selectedCherryPickId={selectedCherryPickId ?? undefined}
              focusedCommitSha={focusedCommitSha}
              focusedBranchName={focusedBranchName}
              onVisibleShas={handleVisibleShas}
              onCherryPickHover={(hover) => {
                if (cpTooltipHideRef.current) clearTimeout(cpTooltipHideRef.current);
                if (!hover) {
                  cpTooltipHideRef.current = setTimeout(() => setCpTooltip(null), 120);
                  return;
                }
                const cp = graph.cherryPicks.find((c) => c.id === hover.id);
                if (!cp) return;
                setCpTooltip({ cp, anchorX: hover.x, rowTop: hover.y, rowBottom: hover.y, readOnly: true });
              }}
              highlightedCherryPickId={cpTooltip?.cp.id ?? null}
              jiraBaseUrl={jiraAccessQuery.data?.jiraBaseUrl ?? null}
              onBranchContextMenu={(branch, evt) => {
                evt.preventDefault?.();
                deleteBranch.reset();
                setBranchMenu({ x: evt.clientX, y: evt.clientY, ...branch });
              }}
              // Menus ouverts au clic (branchMenu/menu) se ferment déjà entre eux tout seuls via
              // leur propre listener "click outside" — seule la tooltip de commit au survol
              // (éphémère, pas de listener équivalent) a besoin de ce callback explicite.
              onCommitHover={() => { setMenu(null); setBranchMenu(null); }}
              theme={preferencesQuery.data?.theme ?? 'dark'}
              branchColorRules={repoMetaQuery.data?.branchColorRules}
              onConfigureCi={() => setCiConfigOpen(true)}
            />
          )}
        </div>
      </div>


      {ciConfigOpen && (
        <Suspense fallback={null}>
          <CiConfigModal
            repoId={repoId}
            config={repoMetaQuery.data?.ciConfig ?? null}
            canEdit={canEditBranchColors}
            onClose={() => setCiConfigOpen(false)}
          />
        </Suspense>
      )}

      {traceabilityOpen && (
        <Suspense fallback={null}>
          <TraceabilityModal
            repoId={repoId}
            srsPageId={repoMetaQuery.data?.srsPageId ?? null}
            sddPageId={repoMetaQuery.data?.sddPageId ?? null}
            canEdit={canEditBranchColors}
            jiraBaseUrl={jiraAccessQuery.data?.jiraBaseUrl ?? null}
            onFocusCommit={(sha) => { setFocusedCommitSha(sha); setTraceabilityOpen(false); }}
            onClose={() => setTraceabilityOpen(false)}
          />
        </Suspense>
      )}

      <RepoViewModals
        repoId={repoId}
        graph={graph}
        canMarkRangeEnd={canMarkRangeEnd}
        canCreateBranch={canCreateBranch}
        canCreateTag={canCreateTag}
        canDeleteTag={canDeleteTag}
        canDeleteBranch={canDeleteBranch}
        canEditBranchColors={canEditBranchColors}
        aiEnabled={aiEnabled}
        branchColorRules={repoMetaQuery.data?.branchColorRules ?? []}
        menu={menu}
        setMenu={setMenu}
        rangeStart={rangeStart}
        setRangeStart={setRangeStart}
        setRangeEnd={setRangeEnd}
        tagMenu={tagMenu}
        setTagMenu={setTagMenu}
        deleteTag={deleteTag}
        branchMenu={branchMenu}
        setBranchMenu={setBranchMenu}
        deleteBranch={deleteBranch}
        cherryPickSource={cherryPickSource}
        setCherryPickSource={setCherryPickSource}
        createBranchSource={createBranchSource}
        setCreateBranchSource={setCreateBranchSource}
        createTagSource={createTagSource}
        setCreateTagSource={setCreateTagSource}
        branchColorRulesOpen={branchColorRulesOpen}
        setBranchColorRulesOpen={setBranchColorRulesOpen}
        summarizeCommit={summarizeCommit}
        summarizeRange={summarizeRange}
        summaryTitle={summaryTitle}
        setSummaryTitle={setSummaryTitle}
        summaryData={summaryData}
        setSummaryData={setSummaryData}
        setSelected={setSelected}
        onRefetchGraph={() => graphQuery.refetch()}
        showAiHistory={showAiHistory}
        setShowAiHistory={setShowAiHistory}
        setFocusedCommitSha={setFocusedCommitSha}
      />
    </>
  );
}
